import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate, todayLocal } from "@/lib/api";
import { In, Sel } from "@/pages/Projects";
import { Plus, Trash2, Landmark, TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const METODO_LABEL = { efectivo: "Efectivo", transferencia: "Transferencia", tarjeta_credito: "Tarjeta crédito", tarjeta_debito: "Tarjeta débito", cheque: "Cheque", comisiones: "Comisiones cambios", otro: "Otro" };

const Row = ({ label, value, color, bold }) => (
  <div className="flex items-center justify-between px-4 py-2" style={{ borderTop: "1px solid var(--border)" }}>
    <span className={`text-xs ${bold ? "font-bold text-slate-100" : "text-slate-400"}`}>{label}</span>
    <span className={`font-mono text-sm ${bold ? "font-bold" : ""}`} style={{ color: color || "var(--text)" }}>{fmtMXN(value)}</span>
  </div>
);

export const TreasuryTab = () => {
  const now = new Date();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [others, setOthers] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ description: "", amount: "", income_date: todayLocal(), payment_method: "transferencia", notes: "" });

  const load = async () => {
    try {
      const [{ data: s }, { data: o }, { data: m }] = await Promise.all([
        api.get("/treasury/summary", { params: { year, month } }),
        api.get("/treasury/other-incomes"),
        api.get("/treasury/monthly", { params: { year } }),
      ]);
      setData(s); setOthers(o || []); setMonthly((m || []).map((r) => ({ ...r, name: MESES[r.month - 1].slice(0, 3) })));
    } catch { toast.error("Error al cargar la Caja General"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const saveOther = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      await api.post("/treasury/other-incomes", { ...f, amount: Number(f.amount) });
      toast.success("Ingreso registrado");
      setShowForm(false);
      setF({ description: "", amount: "", income_date: todayLocal(), payment_method: "transferencia", notes: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al registrar"); }
    finally { setSaving(false); }
  };

  const removeOther = async (id) => {
    if (!window.confirm("¿Eliminar este ingreso?")) return;
    try { await api.delete(`/treasury/other-incomes/${id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  const h = data.historico; const p = data.periodo;

  return (
    <div className="space-y-4" data-testid="treasury-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="panel p-4" data-testid="treasury-kpi-saldo">
          <div className="label-mono flex items-center gap-1.5" style={{ color: "var(--cyan)" }}><Landmark className="w-3.5 h-3.5" /> SALDO GENERAL</div>
          <div className="font-mono font-bold text-2xl mt-1" style={{ color: data.saldo_general >= 0 ? "var(--cyan)" : "#ef4444" }}>{fmtMXN(data.saldo_general)}</div>
        </div>
        <div className="panel p-4" data-testid="treasury-kpi-ingresos">
          <div className="label-mono flex items-center gap-1.5" style={{ color: "var(--green)" }}><TrendingUp className="w-3.5 h-3.5" /> INGRESOS TOTALES</div>
          <div className="font-mono font-bold text-lg mt-1" style={{ color: "var(--green)" }}>{fmtMXN(h.ingresos.total)}</div>
        </div>
        <div className="panel p-4" data-testid="treasury-kpi-egresos">
          <div className="label-mono flex items-center gap-1.5" style={{ color: "#ef4444" }}><TrendingDown className="w-3.5 h-3.5" /> EGRESOS TOTALES</div>
          <div className="font-mono font-bold text-lg mt-1" style={{ color: "#ef4444" }}>{fmtMXN(h.egresos.total)}</div>
        </div>
        <div className="panel p-4" data-testid="treasury-kpi-efectivo">
          <div className="label-mono" style={{ color: "var(--amber, #d97706)" }}>SUB-CAJA EFECTIVO</div>
          <div className="font-mono font-bold text-lg mt-1" style={{ color: "var(--amber, #d97706)" }}>{fmtMXN(data.efectivo?.saldo_caja)}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Por comprobar: {fmtMXN(data.efectivo?.por_comprobar)}</div>
        </div>
      </div>
      <div className="text-[11px] text-slate-500">
        El saldo general es histórico: todos los ingresos menos todos los egresos (cualquier método de pago, sin rechazados; los gastos a crédito cuentan por mensualidad pagada). Los cambios a efectivo son traspasos internos a la sub-caja — solo la comisión cuenta como egreso. Crédito pendiente por pagar: <b className="text-slate-300">{fmtMXN(h.credito_pendiente)}</b>.
      </div>

      <div className="flex items-center gap-2">
        <span className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>// DESGLOSE DEL MES</span>
        <Sel value={String(month)} onChange={(v) => setMonth(Number(v))} testId="treasury-month"
          options={MESES.map((m, i) => ({ value: String(i + 1), label: m }))} />
        <Sel value={String(year)} onChange={(v) => setYear(Number(v))} testId="treasury-year"
          options={[year - 2, year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => ({ value: String(y), label: String(y) }))} />
      </div>

      <div className="panel p-4" data-testid="treasury-flow-chart">
        <div className="label-mono mb-3" style={{ color: "var(--cyan)" }}>// FLUJO MENSUAL {year} · INGRESOS VS EGRESOS</div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={monthly} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
            <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "rgba(148,163,184,0.2)" }} tickLine={false} />
            <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} axisLine={false} tickLine={false} width={52} />
            <Tooltip
              formatter={(v, n) => [fmtMXN(v), { ingresos: "Ingresos", egresos: "Egresos" }[n] || n]}
              labelFormatter={(l, p) => `${MESES[(p?.[0]?.payload?.month || 1) - 1]} ${year} · Flujo: ${fmtMXN(p?.[0]?.payload?.flujo)}`}
              contentStyle={{ background: "#0c1424", border: "1px solid rgba(34,211,238,0.3)", borderRadius: 4, fontSize: 12 }}
              labelStyle={{ color: "#e2e8f0" }} cursor={{ fill: "rgba(148,163,184,0.06)" }}
            />
            <Legend formatter={(v) => <span style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{v === "ingresos" ? "Ingresos" : "Egresos"}</span>} />
            <Bar dataKey="ingresos" fill="#22c55e" radius={[3, 3, 0, 0]} maxBarSize={26} />
            <Bar dataKey="egresos" fill="#ef4444" radius={[3, 3, 0, 0]} maxBarSize={26} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="panel" data-testid="treasury-ingresos-panel">
          <div className="panel-header px-4 py-2 label-mono" style={{ color: "var(--green)" }}>INGRESOS · {MESES[month - 1]} {year}</div>
          <Row label="Obras (anticipos/estimaciones)" value={p.ingresos.obras} />
          <Row label="Taller (cotizaciones pagadas)" value={p.ingresos.taller} />
          <Row label="Otros ingresos" value={p.ingresos.otros} />
          <Row label="Total del mes" value={p.ingresos.total} color="var(--green)" bold />
        </div>
        <div className="panel" data-testid="treasury-egresos-panel">
          <div className="panel-header px-4 py-2 label-mono" style={{ color: "#ef4444" }}>EGRESOS · {MESES[month - 1]} {year}</div>
          <Row label="Gastos de obra (incluye nómina y destajos)" value={p.egresos.gastos_obra} />
          <Row label="Gastos administrativos" value={p.egresos.gastos_admin} />
          <Row label="Comisiones por cambios" value={p.egresos.comisiones_cambios} />
          <Row label="Total del mes" value={p.egresos.total} color="#ef4444" bold />
          <Row label="Flujo del mes (ingresos − egresos)" value={p.flujo} color={p.flujo >= 0 ? "var(--cyan)" : "#ef4444"} bold />
        </div>
        <div className="panel" data-testid="treasury-metodos-panel">
          <div className="panel-header px-4 py-2 label-mono" style={{ color: "var(--amber, #d97706)" }}>EGRESOS POR MÉTODO · {MESES[month - 1]} {year}</div>
          {Object.keys(p.egresos_por_metodo || {}).length === 0 && <div className="px-4 py-3 text-xs text-slate-500">Sin egresos este mes.</div>}
          {Object.entries(p.egresos_por_metodo || {}).sort((a, b) => b[1] - a[1]).map(([m, v]) => (
            <Row key={m} label={METODO_LABEL[m] || m} value={v} />
          ))}
          <Row label="Traspaso a caja de efectivo (info)" value={p.traspasos_a_efectivo} color="var(--amber, #d97706)" />
        </div>
      </div>

      <div className="panel" data-testid="treasury-other-incomes">
        <div className="panel-header px-4 py-2 flex items-center justify-between">
          <span className="label-mono" style={{ color: "var(--green)" }}>OTROS INGRESOS (NO LIGADOS A OBRA)</span>
          <button data-testid="other-income-new" onClick={() => setShowForm(!showForm)} className="btn-primary px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5">
            <Plus className="w-3 h-3" /> Registrar ingreso
          </button>
        </div>
        {showForm && (
          <form onSubmit={saveOther} className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" style={{ borderBottom: "1px solid var(--border)" }}>
            <In label="Descripción" testId="other-income-desc" value={f.description} onChange={(v) => setF({ ...f, description: v })} required />
            <In label="Monto" testId="other-income-amount" type="number" value={f.amount} onChange={(v) => setF({ ...f, amount: v })} required />
            <In label="Fecha" testId="other-income-date" type="date" value={f.income_date} onChange={(v) => setF({ ...f, income_date: v })} required />
            <Sel label="Método" testId="other-income-method" value={f.payment_method} onChange={(v) => setF({ ...f, payment_method: v })}
              options={[{ value: "transferencia", label: "Transferencia" }, { value: "efectivo", label: "Efectivo" }, { value: "cheque", label: "Cheque" }, { value: "otro", label: "Otro" }]} />
            <button type="submit" disabled={saving} data-testid="other-income-save" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold disabled:opacity-50">
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </form>
        )}
        {others.length === 0 ? (
          <div className="px-4 py-4 text-xs text-slate-500">Sin otros ingresos registrados.</div>
        ) : (
          <table className="w-full text-xs">
            <tbody>
              {others.map((o) => (
                <tr key={o.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`other-income-${o.id}`}>
                  <td className="px-4 py-2 font-mono text-slate-400 whitespace-nowrap">{fmtDate(o.income_date)}</td>
                  <td className="px-4 py-2 text-slate-200">{o.description}</td>
                  <td className="px-4 py-2 text-slate-500 uppercase text-[10px]">{o.payment_method}</td>
                  <td className="px-4 py-2 text-right font-mono" style={{ color: "var(--green)" }}>{fmtMXN(o.amount)}</td>
                  <td className="px-2 py-2 text-right">
                    <button data-testid={`other-income-delete-${o.id}`} onClick={() => removeOther(o.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TreasuryTab;
